import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles, Euro, PenLine } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = {
  assistant: {
    label: "Question",
    icon: Sparkles,
    placeholder: "Posez votre question sur nos services…",
    greeting: "Bonjour ! Je suis l'assistant DMZ Normandie. Une question sur nos travaux de maçonnerie à Bernay et dans l'Eure ?",
  },
  estimation: {
    label: "Estimation",
    icon: Euro,
    placeholder: "Décrivez votre projet (surface, matériaux, lieu)…",
    greeting: "Décrivez-moi votre projet en quelques lignes et je vous donne immédiatement une fourchette de prix indicative.",
  },
  redaction: {
    label: "Rédaction",
    icon: PenLine,
    placeholder: "Collez vos notes brutes sur un chantier…",
    greeting: "Envoyez vos notes de chantier (lieu, travaux, matériaux) et je les transforme en description élégante pour votre portfolio.",
  },
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("assistant");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState({ assistant: [], estimation: [], redaction: [] });
  const sessionId = useRef(localStorage.getItem("dmz-chat-session") || (() => {
    const id = crypto.randomUUID();
    localStorage.setItem("dmz-chat-session", id);
    return id;
  })());
  const bottomRef = useRef(null);
  const current = messages[mode];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [current.length, current[current.length - 1]?.text]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    const withUser = [...current, { role: "user", text }];
    setMessages((m) => ({ ...m, [mode]: [...withUser, { role: "assistant", text: "" }] }));

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, mode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);
            const chunk = parsed.delta ? parsed.delta : parsed.error ? "Désolé, une erreur est survenue. Appelez-nous au 07 45 04 91 89." : "";
            if (chunk) {
              setMessages((m) => {
                const arr = [...m[mode]];
                arr[arr.length - 1] = { role: "assistant", text: arr[arr.length - 1].text + chunk };
                return { ...m, [mode]: arr };
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const arr = [...m[mode]];
        arr[arr.length - 1] = { role: "assistant", text: "Connexion impossible. Réessayez ou appelez-nous au 07 45 04 91 89." };
        return { ...m, [mode]: arr };
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.4, duration: 0.6 }}
        onClick={() => setOpen(!open)}
        data-testid="chat-open-button"
        aria-label="Ouvrir l'assistant IA"
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full bg-[#C4A484] text-[#0A0A0A] flex items-center justify-center shadow-lg shadow-black/50 hover:bg-[#A68A75] transition-colors duration-300"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[70] w-[calc(100vw-3rem)] max-w-[400px] h-[520px] max-h-[70vh] bg-[#0D0D0D] border border-white/10 flex flex-col shadow-2xl shadow-black/60"
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-serif-display font-light text-lg tracking-tight">Assistant <em className="text-accent-sand">DMZ</em></p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">Propulsé par Claude IA</p>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-500" title="En ligne" />
              </div>
              <div className="flex gap-2" data-testid="chat-mode-switcher">
                {Object.entries(MODES).map(([key, m]) => {
                  const Icon = m.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setMode(key)}
                      data-testid={`chat-mode-${key}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border transition-colors duration-300 ${
                        mode === key ? "border-[#C4A484] text-[#C4A484]" : "border-white/10 text-zinc-500 hover:text-zinc-300"
                      }`}
                    >
                      <Icon size={11} /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
              <div className="max-w-[85%] text-sm font-light leading-relaxed text-zinc-300 bg-white/[0.04] border border-white/10 px-4 py-3">
                {MODES[mode].greeting}
              </div>
              {current.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${msg.role}-${i}`}
                  className={`max-w-[85%] text-sm font-light leading-relaxed px-4 py-3 whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "ml-auto bg-[#C4A484] text-[#0A0A0A]"
                      : "text-zinc-300 bg-white/[0.04] border border-white/10"
                  }`}
                >
                  {msg.text}
                  {msg.role === "assistant" && loading && i === current.length - 1 && !msg.text && (
                    <Loader2 size={14} className="animate-spin text-zinc-500" />
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {mode === "assistant" && (
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="chat-devis-link"
                className="mx-5 mb-2 text-[10px] tracking-[0.2em] uppercase text-[#C4A484] hover:text-[#A68A75] transition-colors"
              >
                → Demander un devis gratuit
              </Link>
            )}

            <div className="p-4 border-t border-white/10 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={MODES[mode].placeholder}
                data-testid="chat-message-input"
                className="flex-1 bg-white/[0.03] border border-white/10 px-4 py-3 text-sm font-light text-[#F5F5F0] placeholder:text-zinc-600 focus:outline-none focus:border-[#C4A484] transition-colors duration-300"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Envoyer"
                className="w-12 bg-[#C4A484] text-[#0A0A0A] flex items-center justify-center hover:bg-[#A68A75] transition-colors duration-300 disabled:opacity-50 shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
